/* © NXAPP Limited */
import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import { Sparkles, Copy, CheckCircle, ArrowRight } from 'lucide-react'
import Spinner from '@/components/ui/Spinner'
import { useNavigate } from 'react-router-dom'
import type { SegmentRow } from '@/types/rows'
import { authedFetch } from '@/lib/authedFetch'

export default function AiDraftPage() {
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const [brief, setBrief] = useState({
    trends: '',
    season: new Date().toLocaleString('default', { month: 'long' }),
    industry: '',
    app: '',
  })
  const [draft, setDraft] = useState<{ subject: string; html: string } | null>(null)
  const [copied, setCopied] = useState(false)
  const [segmentId, setSegmentId] = useState('')

  const { data: segments } = useQuery({
    queryKey: ['segments', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('segments').select('id, name').eq('user_id', user!.id)
      return (data ?? []) as Pick<SegmentRow, 'id' | 'name'>[]
    },
    enabled: !!user,
  })

  const generate = useMutation({
    mutationFn: async () => {
      const res = await authedFetch('/api/ai-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brief, segmentId: segmentId || null }),
      })
      if (!res.ok) throw new Error(await res.text())
      return res.json() as Promise<{ subject: string; html: string }>
    },
    onSuccess: (data) => setDraft(data),
  })

  async function useAsDraft() {
    if (!draft || !user) return

    // Wrap the AI HTML in a single Unlayer "Custom HTML" content block so the
    // builder loads with the generated content visible and editable.
    const design_json = {
      schemaVersion: 16,
      body: {
        id: 'body',
        rows: [
          {
            id: 'u_row_1',
            cells: [1],
            columns: [
              {
                id: 'u_column_1',
                contents: [
                  {
                    id: 'u_content_html_1',
                    type: 'html',
                    values: {
                      html: draft.html,
                      containerPadding: '0px',
                      _meta: { htmlID: 'u_content_html_1', htmlClassNames: 'u_content_html' },
                    },
                  },
                ],
                values: {
                  _meta: { htmlID: 'u_column_1', htmlClassNames: 'u_column' },
                  border: {}, padding: '0px', backgroundColor: '',
                },
              },
            ],
            values: {
              _meta: { htmlID: 'u_row_1', htmlClassNames: 'u_row' },
              backgroundColor: '', columnsBackgroundColor: '',
              backgroundImage: { url: '', fullWidth: true, repeat: 'no-repeat', size: 'custom', position: 'center' },
              padding: '0px',
            },
          },
        ],
        values: {
          textColor: '#000000',
          backgroundColor: '#f5f5f5',
          backgroundImage: { url: '', fullWidth: true, repeat: 'no-repeat', size: 'custom', position: 'center' },
          contentWidth: '600px',
          contentAlign: 'center',
          fontFamily: { label: 'Arial', value: 'arial,helvetica,sans-serif' },
          _meta: { htmlID: 'u_body', htmlClassNames: 'u_body' },
        },
      },
    }

    const { data } = await supabase.from('issues').insert({
      user_id: user.id,
      subject: draft.subject,
      html: draft.html,
      design_json,
      status: 'DRAFT',
      segment_id: segmentId || null,
    }).select().single()

    if (data) navigate(`/issues/${(data as { id: string }).id}/edit`)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-brand-600" /> AI Draft
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Describe this week's brief and the AI will draft your newsletter. You approve before anything sends.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Brief form */}
        <div className="card p-5 space-y-4">
          <h2 className="font-semibold text-gray-900">Weekly brief</h2>

          <div>
            <label className="label">Current tech trends / news <span className="text-red-500">*</span></label>
            <textarea
              className="input min-h-[80px] resize-none"
              placeholder="e.g. OpenAI launches o3, London fintech funding up 40%, new UK AI regulation draft…"
              value={brief.trends}
              onChange={e => setBrief(b => ({ ...b, trends: e.target.value }))}
            />
          </div>

          <div>
            <label className="label">App to feature this week</label>
            <input
              className="input"
              placeholder="e.g. Atlas CRM — one-line description"
              value={brief.app}
              onChange={e => setBrief(b => ({ ...b, app: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Season / calendar context</label>
              <input
                className="input"
                placeholder="e.g. July, Q3 budget season"
                value={brief.season}
                onChange={e => setBrief(b => ({ ...b, season: e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Target industry angle</label>
              <input
                className="input"
                placeholder="e.g. Fintech, general tech"
                value={brief.industry}
                onChange={e => setBrief(b => ({ ...b, industry: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="label">Segment (optional)</label>
            <select className="input" value={segmentId} onChange={e => setSegmentId(e.target.value)}>
              <option value="">All subscribers</option>
              {segments?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          <button
            className="btn-primary w-full justify-center"
            disabled={!brief.trends || generate.isPending}
            onClick={() => generate.mutate()}
          >
            {generate.isPending ? (
              <><Spinner className="w-4 h-4 border-white border-t-transparent" /> Generating…</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Generate draft</>
            )}
          </button>

          {generate.isError && (
            <p className="text-sm text-red-600">Generation failed. Check your Anthropic API key in the Pages env vars.</p>
          )}
        </div>

        {/* Draft output */}
        <div className="card p-5 flex flex-col gap-4">
          <h2 className="font-semibold text-gray-900">Generated draft</h2>

          {!draft ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
              Fill in the brief and click Generate →
            </div>
          ) : (
            <>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Subject line</p>
                <p className="font-semibold text-gray-900">{draft.subject}</p>
              </div>

              <div className="flex-1">
                <p className="text-xs font-medium text-gray-500 mb-1">HTML preview</p>
                <div className="border border-gray-200 rounded-lg overflow-hidden h-64 overflow-y-auto bg-white">
                  <iframe
                    srcDoc={draft.html}
                    className="w-full h-full"
                    sandbox="allow-same-origin"
                    title="Email preview"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    await navigator.clipboard.writeText(draft.html)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                  }}
                  className="btn-secondary flex-1 justify-center"
                >
                  {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy HTML'}
                </button>
                <button onClick={useAsDraft} className="btn-primary flex-1 justify-center">
                  <ArrowRight className="w-4 h-4" /> Use as draft issue
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
